import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  ArrowPathIcon,
  CalculatorIcon,
} from '@heroicons/react/24/outline';
import { nibApi } from '@services/api';
import { SubmissionList } from '@components/nib/SubmissionList';
import { Pagination } from '@components/history/Pagination';
import { ConfirmModal } from '@components/common/ConfirmModal';
import type {
  NIBSubmission,
  NIBSubmissionList,
  NIBSubmissionFilters,
  NIBSubmissionStatus,
} from '@/types';
import { NIB_STATUS_LABELS } from '@/types';

interface FilterValues {
  status?: NIBSubmissionStatus;
  period_year?: number;
  period_month?: number;
  search?: string;
}

export default function NIBSubmissions() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [submissionList, setSubmissionList] =
    useState<NIBSubmissionList | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [submissionToDelete, setSubmissionToDelete] =
    useState<NIBSubmission | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Parse filters from URL
  const getFiltersFromUrl = (): FilterValues => {
    const filters: FilterValues = {};

    const status = searchParams.get('status');
    if (status) filters.status = status as NIBSubmissionStatus;

    const periodYear = searchParams.get('period_year');
    if (periodYear) filters.period_year = parseInt(periodYear, 10);

    const periodMonth = searchParams.get('period_month');
    if (periodMonth) filters.period_month = parseInt(periodMonth, 10);

    const search = searchParams.get('search');
    if (search) filters.search = search;

    return filters;
  };

  const [filters, setFilters] = useState<FilterValues>(getFiltersFromUrl());
  const [currentPage, setCurrentPage] = useState(
    parseInt(searchParams.get('page') || '1', 10)
  );
  const [itemsPerPage, setItemsPerPage] = useState(
    parseInt(searchParams.get('per_page') || '25', 10)
  );

  // Update URL when filters/pagination change
  const updateUrl = useCallback(
    (newFilters: FilterValues, page: number, perPage: number) => {
      const params = new URLSearchParams();

      if (newFilters.status) params.set('status', newFilters.status);
      if (newFilters.period_year)
        params.set('period_year', newFilters.period_year.toString());
      if (newFilters.period_month)
        params.set('period_month', newFilters.period_month.toString());
      if (newFilters.search) params.set('search', newFilters.search);
      if (page > 1) params.set('page', page.toString());
      if (perPage !== 25) params.set('per_page', perPage.toString());

      setSearchParams(params);
    },
    [setSearchParams]
  );

  // Fetch submissions data
  const fetchSubmissions = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: NIBSubmissionFilters = {
        page: currentPage,
        per_page: itemsPerPage,
        ...filters,
      };

      const data = await nibApi.getSubmissions(params);
      setSubmissionList(data);
    } catch (error) {
      console.error('Failed to fetch submissions:', error);
      toast.error('Failed to load NIB submissions');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, itemsPerPage, filters]);

  // Initial load
  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchSubmissions();
    }, 10000);

    return () => clearInterval(interval);
  }, [autoRefresh, fetchSubmissions]);

  // Debounced search
  useEffect(() => {
    if (!filters.search) return;

    const timeout = setTimeout(() => {
      setCurrentPage(1);
      updateUrl(filters, 1, itemsPerPage);
    }, 500);

    return () => clearTimeout(timeout);
  }, [filters, filters.search, itemsPerPage, updateUrl]);

  const handleFilterChange = (
    field: keyof FilterValues,
    value: string | number | undefined
  ) => {
    const newFilters: FilterValues = { ...filters };

    if (value === '' || value === undefined) {
      delete newFilters[field];
    } else {
      // @ts-expect-error - FilterValues allows string or number for some fields
      newFilters[field] = value;
    }

    setFilters(newFilters);
    setCurrentPage(1);
    updateUrl(newFilters, 1, itemsPerPage);
  };

  const handleClearFilters = () => {
    const emptyFilters: FilterValues = {};
    setFilters(emptyFilters);
    setCurrentPage(1);
    updateUrl(emptyFilters, 1, itemsPerPage);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    updateUrl(filters, page, itemsPerPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (perPage: number) => {
    setItemsPerPage(perPage);
    setCurrentPage(1);
    updateUrl(filters, 1, perPage);
  };

  const handleViewSubmission = (submission: NIBSubmission) => {
    navigate(`/nib-c10/submissions/${submission.id}`);
  };

  const handleDeleteClick = (submission: NIBSubmission) => {
    setSubmissionToDelete(submission);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!submissionToDelete) return;

    setIsDeleting(true);
    try {
      await nibApi.deleteSubmission(submissionToDelete.id);
      toast.success('Submission deleted successfully');
      setDeleteModalOpen(false);
      setSubmissionToDelete(null);

      // Refresh the list
      fetchSubmissions();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete submission');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSubmissionToDelete(null);
  };

  // Generate year options for filter (current year and 5 years back)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  // Month options
  const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const hasActiveFilters = Object.keys(filters).length > 0;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                NIB C10 Submissions
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Create and manage NIB C10 contribution submissions
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Auto-refresh toggle */}
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={e => setAutoRefresh(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Auto-refresh (10s)
              </label>
              {/* Manual refresh button */}
              <button
                onClick={fetchSubmissions}
                disabled={isLoading}
                className="inline-flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowPathIcon
                  className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
                />
                Refresh
              </button>
              {/* Calculator button */}
              <button
                onClick={() => navigate('/nib-c10/calculator')}
                className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                <CalculatorIcon className="h-5 w-5" />
                Calculator
              </button>
              {/* New Submission button */}
              <button
                onClick={() => navigate('/nib-c10/submissions/new')}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <PlusIcon className="h-5 w-5" />
                New Submission
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <label
                htmlFor="search"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Search
              </label>
              <input
                type="text"
                id="search"
                placeholder="Employer name or number..."
                value={filters.search || ''}
                onChange={e => handleFilterChange('search', e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              />
            </div>

            {/* Status Filter */}
            <div>
              <label
                htmlFor="status"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Status
              </label>
              <select
                id="status"
                value={filters.status || ''}
                onChange={e =>
                  handleFilterChange(
                    'status',
                    e.target.value
                      ? (e.target.value as NIBSubmissionStatus)
                      : undefined
                  )
                }
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              >
                <option value="">All Statuses</option>
                {Object.entries(NIB_STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {/* Period Year */}
            <div>
              <label
                htmlFor="period_year"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Period Year
              </label>
              <select
                id="period_year"
                value={filters.period_year || ''}
                onChange={e =>
                  handleFilterChange(
                    'period_year',
                    e.target.value ? parseInt(e.target.value) : undefined
                  )
                }
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              >
                <option value="">All Years</option>
                {yearOptions.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            {/* Period Month */}
            <div>
              <label
                htmlFor="period_month"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Period Month
              </label>
              <select
                id="period_month"
                value={filters.period_month || ''}
                onChange={e =>
                  handleFilterChange(
                    'period_month',
                    e.target.value ? parseInt(e.target.value) : undefined
                  )
                }
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              >
                <option value="">All Months</option>
                {monthOptions.map(month => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <div className="mt-4 flex justify-end">
              <button
                onClick={handleClearFilters}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>

        {/* Results count */}
        {submissionList && !isLoading && (
          <div className="mb-4">
            <p className="text-sm text-gray-700">
              Found <span className="font-medium">{submissionList.total}</span>{' '}
              submission
              {submissionList.total !== 1 ? 's' : ''}
            </p>
          </div>
        )}

        {/* Table */}
        <SubmissionList
          submissions={submissionList?.items || []}
          isLoading={isLoading}
          onView={handleViewSubmission}
          onDelete={handleDeleteClick}
        />

        {/* Pagination */}
        {submissionList && submissionList.total > 0 && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={submissionList.pages}
              totalItems={submissionList.total}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          isOpen={deleteModalOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Delete Submission"
          message={
            submissionToDelete
              ? `Are you sure you want to delete the ${submissionToDelete.employer_name || 'Unknown'} submission for ${new Date(
                  submissionToDelete.period_year,
                  submissionToDelete.period_month - 1
                ).toLocaleDateString('en-US', {
                  month: 'long',
                  year: 'numeric',
                })}? This action cannot be undone.`
              : ''
          }
          confirmLabel="Delete"
          cancelLabel="Cancel"
          variant="danger"
          isLoading={isDeleting}
        />
      </div>
    </div>
  );
}
